import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Card, CardContent } from "@/components/ui/card";
import { Star } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { allTestimonials, averageRating, totalReviews } from "@/data/testimonials";
import ReviewSubmitForm from "@/components/ReviewSubmitForm";
import { lazy, Suspense } from "react";
const FloatingTestCTA = lazy(() => import("@/components/FloatingTestCTA"));
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";

const AvisClients = () => {
  useAffiliateTracking();
  const [visibleCount, setVisibleCount] = useState(12);

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'fill-primary text-primary' : 'text-muted-foreground'}`}
      />
    ));
  };

  const loadMore = () => {
    setVisibleCount(prev => Math.min(prev + 12, allTestimonials.length));
  };

  return (
    <>
      <Helmet>
        <title>Avis Clients Vérifiés | RoiDuStreaming</title>
        <meta name="description" content={`Découvrez les avis de nos ${totalReviews} clients satisfaits avec une note moyenne de ${averageRating}/5.`} />
        <link rel="canonical" href="https://roidustreaming.fr/avis-clients" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumbs />

        {/* Hero */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              ⭐ {totalReviews} Avis Clients <span className="text-primary">Vérifiés</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Découvrez ce que nos {totalReviews} clients satisfaits pensent de notre service
            </p>

            {/* Stats */}
            <div className="inline-flex items-center gap-6 glass-card rounded-2xl px-8 py-6 border border-primary/20">
              <div className="text-center">
                <div className="text-4xl font-black text-primary">{averageRating}/5</div>
                <div className="text-sm text-muted-foreground">Note moyenne</div>
              </div>
              <div className="h-12 w-px bg-border"></div>
              <div className="flex gap-1">
                {renderStars(Math.round(averageRating))}
              </div>
              <div className="h-12 w-px bg-border"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">{totalReviews}</div>
                <div className="text-sm text-muted-foreground">avis</div>
              </div>
            </div>
          </div>
        </section>

        {/* Formulaire d'avis */}
        <section className="px-4 pb-8">
          <div className="container mx-auto max-w-4xl">
            <ReviewSubmitForm />
          </div>
        </section>

        {/* Testimonials Grid */}
        <section className="py-8 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allTestimonials.slice(0, visibleCount).map((testimonial) => (
                <Card
                  key={testimonial.id}
                  className="glass-card border border-border/50 hover:border-primary/30 transition-all duration-300"
                >
                  <CardContent className="p-5">
                    {/* Author & Badge */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-primary-foreground font-bold">
                          {testimonial.author.charAt(0)}
                        </div>
                        <div>
                          <div className="font-semibold text-foreground">{testimonial.author}</div>
                          <div className="flex gap-0.5">
                            {renderStars(testimonial.rating)}
                          </div>
                        </div>
                      </div>
                      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                        {testimonial.badge}
                      </span>
                    </div>

                    {/* Quote */}
                    <p className="text-foreground text-sm leading-relaxed mb-2">
                      "{testimonial.text}"
                    </p>
                    
                    {/* Date */}
                    <p className="text-xs text-muted-foreground">{testimonial.date}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Load More */}
            {visibleCount < allTestimonials.length && (
              <div className="text-center mt-8">
                <Button onClick={loadMore} variant="outline" className="border-primary/30">
                  Voir plus d'avis ({allTestimonials.length - visibleCount} restants)
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-2xl text-center">
            <div className="glass-card rounded-2xl p-8 border border-primary/30">
              <h2 className="text-2xl font-bold mb-4">Rejoignez nos clients satisfaits !</h2>
              <p className="text-muted-foreground mb-6">
                Profitez de +15 000 chaînes, +100 000 films et +25 000 séries pour seulement 5€/mois
              </p>
              <Button
                onClick={() => window.location.href = '/#pricing'}
                className="btn-primary"
                size="lg"
              >
                Voir nos offres
              </Button>
            </div>
          </div>
        </section>

        <Footer />

        <Suspense fallback={null}>
          <FloatingTestCTA />
        </Suspense>
      </div>
    </>
  );
};

export default AvisClients;
